import {
  useAcceptOrderForm,
  useOrderFormAcceptanceQuery,
  useRequestOrderFormAcceptance,
  useTosAcceptanceQuery,
} from '@alice-financial/alice-sdk'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  ExternalLink,
  Fieldset,
  Legend,
  Paper,
  TextFieldController,
  Typography,
} from '@alice-financial/pretext-ui'
import { MaskedInput } from '@alice-financial/pretext-ui/inputs/MaskedInput'
import * as React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useOrg } from '../organization/useOrg'
import { useOrgId } from '../useOrgId'
import { SignaturePad, SignaturePadRef } from './SignaturePad'

type OrderFormValues = {
  ein: string
  location_count: string
  employee_count: string
  accept_org_tos: boolean
  onboarding_timeline: boolean
  admin_title: string
  agreed_to_two_year_term: boolean
  agreed_to_three_year_term: boolean
  is_reference_customer: boolean
}

type OrderFormProps = {
  accepterId: number
}
export const OrderForm = ({ accepterId }: OrderFormProps) => {
  const [signaturePadRef, setSignaturePadRef] = React.useState<SignaturePadRef | null>(null)
  const getSignaturePadRef = React.useCallback((ref: SignaturePadRef) => {
    setSignaturePadRef(ref)
  }, [])
  const ownerId = useOrgId()
  const org = useOrg()
  const {
    mutate: requestOrderFormAcceptance,
    isLoading: isRequestingOrderFormAcceptance,
    isError,
  } = useRequestOrderFormAcceptance({ ownerId, accepterId })
  const { data: orderFormAcceptanceData, isFetching } = useOrderFormAcceptanceQuery({
    ownerId,
    accepterId,
  })
  const { data: orgTosAcceptanceData } = useTosAcceptanceQuery({ ownerId })
  const orgTosAcceptance = orgTosAcceptanceData?.organization?.acceptance
  const acceptance = orderFormAcceptanceData?.organization?.acceptance
  const { control, handleSubmit, watch } = useForm<OrderFormValues>({
    mode: 'all',
    defaultValues: {
      ein: '',
      location_count: '',
      employee_count: '',
      accept_org_tos: false,
      onboarding_timeline: false,
      admin_title: '',
      agreed_to_two_year_term: false,
      agreed_to_three_year_term: false,
      is_reference_customer: false,
    },
  })
  const { mutate: acceptOrderForm, isLoading: isSubmitting } = useAcceptOrderForm(ownerId)
  const twoYearTermValue = watch('agreed_to_two_year_term')
  const threeYearTermValue = watch('agreed_to_three_year_term')
  React.useEffect(() => {
    if (isFetching || acceptance || isRequestingOrderFormAcceptance || isError) return
    requestOrderFormAcceptance()
  }, [acceptance, isRequestingOrderFormAcceptance, requestOrderFormAcceptance, isFetching, isError])

  if (isRequestingOrderFormAcceptance) return <div>Requesting order form acceptance...</div>
  if (!org) return null
  if (acceptance?.withdrawnAt) return <div>Order form withdrawn</div>
  if (!acceptance) return <div>Order form not found</div>
  const tosUrl = orgTosAcceptance?.signedDocument?.url
  if (!tosUrl) return <div>Terms of Service not found</div>

  // return <EmbeddedForm formUrl={acceptance.formUrl} />
  return (
    <Box p={3} component={Paper} elevation={4}>
      <form
        onSubmit={handleSubmit((values) => {
          if (!signaturePadRef) return
          // TODO: notify error if signature is empty
          if (signaturePadRef.signaturePad?.isEmpty()) return
          const signatureSrc = signaturePadRef.signaturePad?.toDataURL() || ''
          acceptOrderForm({
            acceptanceId: acceptance.id,
            acceptEsign: true,
            signatureSrc,
            metadata: values,
          })
        })}
      >
        <Typography variant="body2">
          <strong>Company legal name</strong>: {org.legalName}
        </Typography>
        <Typography variant="body2">
          <strong>Doing business as</strong>: {org.name}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Payroll system</strong>: {org.payrollConnection?.payrollPlatform?.name}
        </Typography>
        <Typography variant="body2">
          <strong>Employer Identification Number (EIN)</strong>:{' '}
          <Controller
            name="ein"
            control={control}
            rules={{
              required: true,
              pattern: {
                value: /^\d{9}$/,
                message: 'EIN must be 9 digits',
              },
            }}
            render={({ field, formState: { submitCount }, fieldState: { error, isTouched } }) => {
              const isError = Boolean((isTouched || submitCount > 0) && error?.message)
              return (
                <MaskedInput
                  {...field}
                  format="##-#######"
                  valueIsNumericString
                  variant="standard"
                  error={isError}
                  helperText={isError ? error?.message : undefined}
                />
              )
            }}
          />
        </Typography>
        <Typography variant="body2">
          <strong>Number of locations*</strong>:{' '}
          <TextFieldController
            variant="standard"
            name="location_count"
            control={control}
            rules={{ required: true }}
            required
          />
        </Typography>
        <Typography variant="body2">
          <strong>Estimated W2 employee count*</strong>:{' '}
          <TextFieldController
            variant="standard"
            name="employee_count"
            control={control}
            rules={{ required: true }}
            required
          />
        </Typography>

        <Box mt={5} mb={3} component={Divider} />
        <Typography variant="h3" gutterBottom>
          Client Administrator Contact Information
        </Typography>
        <Typography variant="body2">
          <strong>Name</strong>: {acceptance.accepter.name}
        </Typography>
        <Typography variant="body2">
          <strong>Email</strong>: {acceptance.accepter.email}
        </Typography>
        <Typography variant="body2">
          <strong>Title*</strong>:{' '}
          <TextFieldController
            name="admin_title"
            variant="standard"
            control={control}
            rules={{ required: true }}
            required
          />
        </Typography>

        <Box mt={5} mb={3} component={Divider} />
        <Fieldset style={{ marginBottom: '1em' }}>
          <Legend>
            <Typography variant="h2" fontWeight="bold">
              Agreements
            </Typography>
          </Legend>
          <Controller
            name="accept_org_tos"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Checkbox
                color="primary"
                required
                label={
                  <>
                    I have read and agree to the{' '}
                    <ExternalLink href={tosUrl} target="_blank">
                      Pretax Spending Administration Terms and Conditions
                    </ExternalLink>
                  </>
                }
                {...field}
                checked={Boolean(field.value)}
              />
            )}
          />
          <Controller
            name="onboarding_timeline"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Checkbox
                color="primary"
                required
                label="I agree to complete the Onboarding Steps within 30 days to avoid Onboarding Fees (required)"
                {...field}
                checked={Boolean(field.value)}
              />
            )}
          />
          <Controller
            name="agreed_to_two_year_term"
            control={control}
            render={({ field }) => (
              <Checkbox
                color="primary"
                label="I would like 2 months free by agreeing to a 24 month Term"
                {...field}
                checked={Boolean(field.value)}
                disabled={threeYearTermValue}
              />
            )}
          />
          <Controller
            name="agreed_to_three_year_term"
            control={control}
            render={({ field }) => (
              <Checkbox
                color="primary"
                label="I would like 3 months free by agreeing to a 36 month Term"
                {...field}
                checked={Boolean(field.value)}
                disabled={twoYearTermValue}
              />
            )}
          />
          <Controller
            name="is_reference_customer"
            control={control}
            render={({ field }) => (
              <Checkbox
                color="primary"
                label="I would like 1 month free by agreeing to be a reference and case study"
                {...field}
                checked={Boolean(field.value)}
              />
            )}
          />
        </Fieldset>
        <Box mt={5} mb={3} component={Divider} />
        <Typography variant="h2" gutterBottom>
          Signature
        </Typography>
        <Typography variant="body2" gutterBottom fontWeight="bold">
          Client
        </Typography>
        <SignaturePad getRef={getSignaturePadRef} />
        <Typography variant="body2">
          {acceptance.accepter.name}
          <br />
          {acceptance.accepter.email}
          <br />
          {new Date().toLocaleDateString()}
        </Typography>
        <Box mt={5} display="flex" justifyContent="center">
          <Button
            variant="contained"
            type="submit"
            disabled={isSubmitting}
            size="large"
            sx={{ width: '30%', minWidth: 200 }}
          >
            Submit
          </Button>
        </Box>
      </form>
    </Box>
  )
}
